/**
 * One place to turn what a postcard says about where it came from into a name the maps,
 * the gallery filter and the URL all agree on.
 *
 * This used to live twice, copied between the home page and the maps page, and the copies
 * had drifted into a bug: six postcards were never shaded on the world map because their
 * spelling did not survive the round trip. The map and the gallery now have to agree —
 * "Germany · 5" on the map opening a gallery of 4 would be a visible lie — so there is one
 * copy and both import it.
 */

import { feature } from "topojson-client";
import type { GeometryCollection, Topology } from "topojson-specification";
import type { Feature, FeatureCollection, GeoJsonProperties, Geometry } from "geojson";
// With the extension, unlike every other import here: both generator scripts load this
// module through Node's own resolver (`node --experimental-strip-types`), which does not
// guess at one.
import { endonyms } from "./endonyms.ts";
// With the type attribute, for the same reason: the generator scripts load this module
// through Node's own resolver, which will not import JSON without being told that is what
// it is. Bundlers accept the attribute and ignore it.
import placeCoordinates from "./place-coordinates.json" with { type: "json" };

export type NamedFeature = Feature<Geometry, { name?: string }>;

/** Unpacks a TopoJSON atlas object into its named features. */
export function namedFeatures(data: unknown, objectName: string) {
  const topology = data as Topology;
  const object = topology.objects[objectName] as GeometryCollection<GeoJsonProperties>;
  const converted = feature(topology, object) as FeatureCollection<Geometry, { name?: string }>;
  return converted.features as NamedFeature[];
}

/**
 * The ASCII spelling of a letter NFD cannot decompose.
 *
 * `normalizePlace` below folds an accent by splitting the letter with NFD and dropping the
 * combining mark. That only works on a letter which has a canonical decomposition, and a
 * whole class has none: strokes and bars through the glyph (Ø, Ł, Đ, Ħ, Ǥ), the ligatures
 * (Æ, Œ, Ĳ, ß), letters in their own right (Þ, Ð, Ə, Ŋ, Ŧ), and dotless ı, whose diacritic
 * is an absence. With no mark to strip they reached the [a-z0-9] filter intact and were
 * deleted with the punctuation: "Føroyar" folded to "f royar" and "Ærø" to "r". That is the
 * same "Åland" → "land" failure the accent fold was written to stop, and it stayed live
 * underneath it, because Å happens to decompose and only looked like the general case.
 *
 * This is a table, not a rule, so it closes the class only as far as its rows reach. A
 * letter that belongs here and is missing is still deleted silently, which is why the test
 * holds every row to the one thing that makes it a row: NFD leaves it alone.
 *
 * The substitutions are the conventional ASCII spellings, so a name stays legible in a URL
 * and a heading rather than merely surviving. Resolving a name in one language to its
 * English name is a different question and not this table's (#47) — but on "f royar" it
 * could not be asked at all, because no alias anyone would recognise can be keyed to a name
 * with a hole in it. It is asked now, in endonyms.ts, and "foroyar" is one of the keys the
 * rows there are hung on.
 */
export const undecomposableLetters: Readonly<Record<string, string | undefined>> = {
  ø: "o", æ: "ae", œ: "oe", ĳ: "ij", ł: "l", đ: "d", ħ: "h", ǥ: "g",
  ı: "i", ß: "ss", þ: "th", ð: "d", ə: "e", ŋ: "n", ŧ: "t",
};

/**
 * Reduces a written place to lowercase ASCII words: "U.S.A." and "u-s-a" both become
 * "u s a", and "Åland Islands" becomes "aland islands".
 *
 * Accents are folded before the character filter, not deleted by it. NFD splits an accented
 * letter into the base letter plus a combining mark, so dropping the marks (U+0300–U+036F)
 * keeps the letter: without that step "Åland" lost its Å entirely and became "land", which
 * no coordinate table or atlas feature could ever match. `undecomposableLetters` above
 * covers the letters NFD has no answer for, which failed the same way for longer — Å only
 * ever looked like the general case because it happens to decompose.
 */
export function normalizePlace(value: string) {
  return value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    // Every letter, rather than a character class listing the table's keys again: a class
    // that fell out of step with the table would splice the literal "undefined" into a
    // name, and the [a-z0-9] filter would keep it.
    .replace(/./gu, (letter) => undecomposableLetters[letter] ?? letter)
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

/**
 * The key the alias tables are looked up by: a normalized name with the spaces taken out.
 *
 * Matching on this instead of on the normalized name takes spacing out of a spelling.
 * "U.S." normalizes to "u s" and "US" to "us"; both key on "us", so the single `us:` row
 * answers for either. Before this, a table had to carry every spacing anyone might write:
 * it had "u s a" but not "u s", so "U.S.A." was recognised and "U.S." was not, and each
 * missing spacing stayed invisible until someone wrote a postcard that way.
 *
 * Stripping the spaces is what `0006_canonical_country.sql` did — it removes `.`, `-`
 * and space outright — which is why that backfill collapsed "U.S." correctly while this
 * module did not, and why the gap hid behind clean stored data. The two agree on
 * punctuation now; they were never going to agree on everything, since the SQL only
 * knows those three characters and folds no accents.
 *
 * Only the lookup ignores spacing. Every name this module returns keeps its spaces,
 * because a normalized name is also what the page prints and what the URL carries.
 */
export function aliasKey(normalized: string) {
  return normalized.replace(/ /g, "");
}

/** Indexes an alias table by `aliasKey` so a lookup ignores spacing. */
function byAliasKey(table: Readonly<Record<string, string>>) {
  return new Map(Object.entries(table).map(([written, canonical]) => [aliasKey(written), canonical]));
}

/**
 * Spellings that need to become the name the map files use.
 *
 * Keys are already normalized and are looked up through `aliasKey`, so a key never
 * carries punctuation and its spacing does not matter: `usa` answers for "USA", "U.S.A."
 * and "u-s-a" alike, and listing "u s a" beside it would be a second row for the same
 * spelling. The test holds the table to both — an unnormalized key, or two keys that mean
 * the same thing to the lookup, fails there.
 *
 * The right-hand side is the one name a country goes by everywhere: postcards and atlas
 * features both pass through this table, so a value has to be a name the site can put on
 * the ground — the normalized name of an atlas feature, or an entry in
 * `unmappedCoordinates` for a country the atlas cannot draw. Those two tables are exactly
 * what `place-coordinates.json` is generated from, and membership in it is where
 * `canonical-country.test.ts` checks the same rule from the Go side.
 *
 * Being an atlas name is not the rule, because a country can be real and unmappable. The
 * 110m atlas has no shape for Hong Kong, Macao, Cabo Verde or the Åland Islands at any
 * spelling — they are under a pixel — and all four are values here, reached through the
 * coordinate table instead. Requiring an atlas name would tell the next person adding an
 * unmappable country that they cannot.
 *
 * Where the atlas does draw the country, the value has to be the atlas's own spelling of
 * it. That is the reason the old `russia: "russian federation"` entry was wrong: the map
 * calls it Russia, so the alias broke a match that worked without it.
 *
 * Where the atlas abbreviates ("Dominican Rep.", "S. Sudan"), the full name is the value
 * and the abbreviation is the key. The value is also what the page prints, so aliasing
 * the other way would have labelled a postcard from the Dominican Republic "Dominican Rep".
 *
 * Exported for the table tests, which hold every key to being normalized and distinct.
 * Nothing should read it directly: a lookup that indexes it by a normalized name is the
 * spacing-sensitive lookup `aliasKey` exists to replace. Use `normalizedCountry`.
 */
export const countryAliases: Readonly<Record<string, string>> = {
  usa: "united states of america",
  "united states": "united states of america",
  "united states of america": "united states of america",
  us: "united states of america",
  uk: "united kingdom",
  "great britain": "united kingdom",
  england: "united kingdom",
  scotland: "united kingdom",
  wales: "united kingdom",
  "northern ireland": "united kingdom",
  "czech republic": "czechia",
  "russian federation": "russia",
  "south korea": "south korea",
  "korea south": "south korea",
  "republic of korea": "south korea",
  "north korea": "north korea",
  "korea north": "north korea",
  "the netherlands": "netherlands",
  holland: "netherlands",
  "hong kong sar": "hong kong",
  "macao sar": "macao",
  macau: "macao",
  "peoples republic of china": "china",
  "republic of china": "taiwan",
  "united arab emirates": "united arab emirates",
  // Not a spacing artifact. "U.S." started resolving the moment the lookup stopped
  // caring about spacing, because the `us` row was already here; "UAE" and "U.A.E."
  // both key on `uae`, and no row for that was ever written.
  uae: "united arab emirates",
  // Two names for one country rather than an abbreviation, but the same missing row.
  "cape verde": "cabo verde",
  "myanmar burma": "myanmar",
  burma: "myanmar",
  // Names the atlas abbreviates. The full name is canonical so it is also what gets printed.
  "bosnia and herz": "bosnia and herzegovina",
  "dominican rep": "dominican republic",
  "s sudan": "south sudan",
  "dem rep congo": "democratic republic of the congo",
  "dr congo": "democratic republic of the congo",
  "democratic republic of congo": "democratic republic of the congo",
  "congo kinshasa": "democratic republic of the congo",
  "republic of the congo": "congo",
  "republic of congo": "congo",
  "congo brazzaville": "congo",
  "central african rep": "central african republic",
  "eq guinea": "equatorial guinea",
  "solomon is": "solomon islands",
  "w sahara": "western sahara",
  "falkland is": "falkland islands",
  "n cyprus": "northern cyprus",
  "fr s antarctic lands": "french southern and antarctic lands",
  macedonia: "north macedonia",
  "republic of north macedonia": "north macedonia",
  "east timor": "timor leste",
  swaziland: "eswatini",
  // The atlas spells it "Côte d'Ivoire", which folds to "cote d ivoire"; no title case can
  // put the accent back, so the English name is the one that gets printed.
  "cote d ivoire": "ivory coast",
  // Folding gets "Türkiye" to "turkiye", but the atlas still calls the country Turkey.
  turkiye: "turkey",
  // "Åland" and "Åland Islands" are the same place, and both fold to a name the atlas
  // cannot draw. This used to be a second key in unmappedCoordinates pointing at the same
  // point, which gave the shorter spelling a coordinate but left it a country of its own:
  // the place list showed "Aland" and "Aland Islands" as two rows and the countries stat
  // counted two. A coordinate table answers "where is this name"; only an alias can say
  // two names are one country.
  aland: "aland islands",
  // Postcrossing's spellings for places with no atlas shape, pointed at the
  // `unmappedCoordinates` key that holds their coordinate. The value is also what the
  // page prints, so it is the readable name rather than the ISO ordering: a card from
  // Tortola is headed "British Virgin Islands", not "Virgin Islands British".
  vatican: "vatican city",
  "holy see": "vatican city",
  "virgin islands british": "british virgin islands",
  "virgin islands u s": "united states virgin islands",
  "saint martin french part": "saint martin",
  "sint maarten dutch part": "sint maarten",
  // The atlas does draw the Falklands, as "Falkland Is."; Postcrossing writes both names.
  "falkland islands malvinas": "falkland islands",
  // Two shapes the atlas draws and Postcrossing names in a different order. Nothing was
  // missing here but the alias: "dem rep congo" is the atlas's wording, and a card
  // written the way Postcrossing writes it fell through to zero miles for want of a key.
  "congo dem rep": "democratic republic of the congo",
  "french southern territories": "french southern and antarctic lands",
  // Postcrossing lists Nauru under its Nauruan name. English is what the page prints,
  // the same call as "cote d ivoire" above.
  naoero: "nauru",
  // English names for three countries that the endonym source spells differently from
  // Postcrossing, and which someone could as easily write on a card: "Caribbean Netherlands"
  // is what the Dutch call those three islands in English, and the other two are the longer
  // and shorter halves of a name.
  //
  // The endonym generator matches the source to this site by English name, so these rows
  // also decide what it can say. Without them it drops all three under "no coordinate for
  // the country itself" — which is false, and printed into endonyms.ts where the next
  // reader would believe it — and "Caribisch Nederland" resolves to nothing.
  "caribbean netherlands": "bonaire sint eustatius and saba",
  "pitcairn islands": "pitcairn",
  "south georgia": "south georgia and the south sandwich islands",
  // Serbian and Montenegrin are written in both scripts, and the endonym source records
  // only the Cyrillic one — "Србија" and "Црна Гора", which fold to nothing. The Latin
  // spelling is the one that can be keyed and the one a Postcrossing profile writes, so
  // these two rows are hand-written for the reason `turkiye` is: the derived table cannot
  // reach them. Kazakh and Ukrainian are not here — their Latin forms are transliterations
  // rather than a second official orthography, so a row for either would be a guess.
  srbija: "serbia",
  "crna gora": "montenegro",
};

/**
 * The fifty states and the District of Columbia, keyed by their postal abbreviation.
 *
 * Keys are looked up through `aliasKey` like the country aliases, so "N.Y.", "N. Y." and
 * "ny" are one spelling and the table needs one row for them. The values are the names
 * the state map draws and the page prints, and they are also the definition of which
 * regions count as US states — `usStateNames` below is built from them rather than from a
 * second list that could fall out of step.
 *
 * Exported for the table tests, which hold every key to being normalized and distinct.
 * Nothing should read it directly: a lookup that indexes it by a normalized name is the
 * spacing-sensitive lookup `aliasKey` exists to replace. Use `normalizedState`.
 */
export const stateAbbreviations: Readonly<Record<string, string>> = {
  al: "alabama", ak: "alaska", az: "arizona", ar: "arkansas", ca: "california", co: "colorado",
  ct: "connecticut", de: "delaware", fl: "florida", ga: "georgia", hi: "hawaii", id: "idaho",
  il: "illinois", in: "indiana", ia: "iowa", ks: "kansas", ky: "kentucky", la: "louisiana",
  me: "maine", md: "maryland", ma: "massachusetts", mi: "michigan", mn: "minnesota",
  ms: "mississippi", mo: "missouri", mt: "montana", ne: "nebraska", nv: "nevada",
  nh: "new hampshire", nj: "new jersey", nm: "new mexico", ny: "new york",
  nc: "north carolina", nd: "north dakota", oh: "ohio", ok: "oklahoma", or: "oregon",
  pa: "pennsylvania", ri: "rhode island", sc: "south carolina", sd: "south dakota",
  tn: "tennessee", tx: "texas", ut: "utah", vt: "vermont", va: "virginia",
  wa: "washington", wv: "west virginia", wi: "wisconsin", wy: "wyoming",
  dc: "district of columbia",
};

export const UNITED_STATES = "united states of america";

const countryAliasesByKey = byAliasKey(countryAliases);
const endonymsByKey = byAliasKey(endonyms.countries);
const stateAbbreviationsByKey = byAliasKey(stateAbbreviations);

/**
 * An alias table in which every name is its own answer, so each one covers its own
 * spacings and nothing else's. `byAliasKey` above indexes a table someone wrote; this
 * indexes a list of names that are already canonical.
 */
function selfAliases(names: readonly string[]) {
  return new Map(names.map((name) => [aliasKey(name), name]));
}

/**
 * Every name this site can put on the ground, each answering for the spacings of itself.
 *
 * `aliasKey` took spacing out of a spelling at the alias tables and nowhere else, so a
 * spelling those tables had a row for was spacing-proof and a spelling they did not was
 * not — and that second set is most of the world. "Viet Nam" is the spelling the UN uses,
 * it folds to "viet nam", the atlas draws "vietnam", and nothing brought the two together:
 * the card reached no coordinate, travelled zero miles, took a row of its own in the place
 * list and a country of its own in the stat. Four symptoms of one missing hop (#48), and
 * 68 of the 250 names below have a space in them to get wrong the same way.
 *
 * Answering with the name rather than merely matching on the key is the point. `aliasKey`
 * is a lookup key and not a spelling — "southkorea" is nobody's — while what comes back
 * from here is printed in a heading, title-cased, and carried in a `?country=` link. So
 * these are alias tables like the ones above, mapping a key to the site's own spelling of
 * it; they are derived rather than written because the atlas already decided these names,
 * and a hand-written row per spacing is exactly what `aliasKey` exists to make unnecessary.
 *
 * Consulting them last is what keeps them additive. Every key in them is a name that
 * already resolved to itself, so a row can only catch a spacing nothing else answered for,
 * and no name a table above resolves to can be intercepted. What would break that is two
 * names colliding once the spaces come out, and place-coordinates.test.ts rejects a
 * coordinate table where that can happen.
 *
 * The source is `place-coordinates.json`, which is generated from this module — so this
 * reads the generator's own output. The loop is cut on the other side rather than trusted:
 * the generator keys its rows with `aliasedCountry`, which stops one table short of here,
 * so nothing this file says can reach the next copy of it. Trusting it instead would have
 * been wrong in a way no test could see, and `aliasedCountry` says how.
 */
const locatableCountriesByKey = selfAliases(Object.keys(placeCoordinates.countries));
const locatableStatesByKey = selfAliases(Object.keys(placeCoordinates.states));

/**
 * The name a written country goes by here, whichever language it arrived in.
 *
 * Three tables answer, and the order between them is the point. `countryAliases` is written
 * by hand, a row at a time, with a reason; `endonyms` is bulk data generated from
 * world-countries, and it can change under us when that package bumps. So the hand table
 * is asked first and a generated row can never overrule it — and endonyms.test.ts fails on
 * a generated row that shadows a hand one, since a row nobody can reach is a row nobody
 * would think to look for.
 *
 * Consulting the second table at all is safe for a narrower reason: every row in it is a
 * name that resolved to nothing before, so it can only add an answer. What would break
 * that is a row keying on a name that already meant somewhere else — Sint Maarten calls
 * itself "Saint-Martin", which is the country 12 km across the island — and both the
 * generator and the test refuse those.
 *
 * `locatableCountriesByKey` is asked last for the reason it is asked at all: it holds no
 * name the two above do not already resolve to, so it can only answer where they both
 * miss, and then only with a name whose spacing was the only thing wrong with what was
 * written. A hand row and a translation both say something about a name; this one says
 * nothing except that spacing is not part of a spelling here either.
 */
export function normalizedCountry(value: string) {
  const name = aliasedCountry(value);
  return locatableCountriesByKey.get(aliasKey(name)) ?? name;
}

/**
 * What a written country resolves to before the coordinate table has an opinion: the two
 * alias tables, and nothing else.
 *
 * It is split out because generate-place-coordinates.mjs keys its rows with it. That
 * script writes the file `locatableCountriesByKey` is built from, so keying with the whole
 * of `normalizedCountry` would let a row an earlier run wrote decide what the next run
 * calls a country. Mostly that is the identity and nobody would notice — until an atlas
 * respelling crosses a space, "Timor-Leste" becoming "TimorLeste", and the stale row
 * answers: the run rewrites the old key, a clean regenerate disagrees, and every test here
 * still passes, because a stale name resolving to itself is exactly what they check.
 *
 * generate-endonyms.mjs draws the same line around its own output one table earlier, and
 * gives the same reason: a generated table must not feed itself.
 *
 * Nothing in the app should call this. What it returns is a name the site may not use.
 */
export function aliasedCountry(value: string) {
  const name = normalizePlace(value);
  const key = aliasKey(name);
  return countryAliasesByKey.get(key) ?? endonymsByKey.get(key) ?? name;
}

/**
 * The name a written region goes by here: the postal abbreviation expanded, or the region
 * spelled the way the state map spells it.
 *
 * The second table is the fifty-six regions us-atlas draws, not the fifty states, because
 * this answers for any region a card names — the state map keys its shapes on the atlas
 * spelling, and Puerto Rico has one of those without being a state. Whether the answer is
 * one of the fifty is `isUsState`'s question, asked after this one.
 */
export function normalizedState(value: string) {
  const name = normalizePlace(value);
  const key = aliasKey(name);
  return stateAbbreviationsByKey.get(key) ?? locatableStatesByKey.get(key) ?? name;
}

/**
 * The names the state map can actually draw: the fifty states and the District of Columbia.
 *
 * us-atlas also ships Puerto Rico, Guam and three more territories, but geoAlbersUsa clips
 * them away, so counting by atlas membership would raise the "US states" stat for a card
 * that shades nothing. The abbreviation table already names exactly this set, so it is the
 * source rather than a second list to keep in step with it.
 */
const usStateNames = new Set(Object.values(stateAbbreviations));

/** True when a written region is one of the fifty states or DC, however it was spelled. */
export function isUsState(value: string) {
  return usStateNames.has(normalizedState(value));
}

/**
 * Turns a normalized name into a URL-friendly one, and back.
 *
 * `fromParam` runs the same normalisation as everything else, so a hand-typed
 * `?country=Japan` works as well as the `?country=united-states-of-america` the map links
 * to, and a stale link cannot select something the map would name differently. Stale is
 * the operative word: a link is the one output of this module that outlives the release
 * that made it, which is why `countryFromParam` also answers for spellings the fold no
 * longer produces.
 */
export function toParam(normalized: string) {
  return normalized.replace(/ /g, "-");
}

/**
 * Place names whose written spelling carries a diacritic.
 *
 * The fold this module shipped until Sep 2, 2026 replaced every non-[a-z0-9] character
 * with a space, which took accented letters with it: "Côte d'Ivoire" folded to
 * "c te d ivoire" and "Åland Islands" to "land islands". Those names went into `?country=`
 * links, and a link lives in someone's messages long after the code that minted it.
 *
 * The list exists so `legacyCountryParams` can put each spelling back through the old fold
 * rather than have someone type "cura ao" out by hand and get it subtly wrong.
 *
 * It is drawn from three places, and only the first is machine-checked. The atlas is: a
 * 110m feature name whose spelling the accent fold changed and which is missing from this
 * list fails the test, and "Côte d'Ivoire" is the only such name there is. Second is
 * `unmappedCoordinates`, for the countries the atlas cannot draw — Åland Islands, Curaçao,
 * Réunion, São Tomé and Príncipe — which it stores already folded, so their written
 * spellings have nowhere else to live.
 *
 * Third, and the one to remember, is the keys of `countryAliases`. "Türkiye" is here
 * because of the `turkiye:` row and nothing else: the atlas draws that country as
 * "Turkey", and the coordinate table has no entry for it, so neither of the other two
 * sources mentions it. The `aland` row added alongside this list is the same kind. An
 * accented key added to either hand table needs its spelling added here as well.
 *
 * Two of those three sources are now checked rather than remembered. An atlas name the
 * fold respells is derived from the atlas below; an accented name Postcrossing writes is
 * derived from the sender fixture in postcrossing-senders.test.ts, which is the only
 * place in the repo that still holds the accents — the hand tables store their keys
 * already folded, so "saint barthelemy" has no way to say it was written with an é. That
 * check exists because "Saint Barthélemy" was added to `unmappedCoordinates` without its
 * spelling and the suite stayed green.
 *
 * What is still only remembered is a spelling neither source writes with accents. "São
 * Tomé and Príncipe" is the one today — Postcrossing spells it ASCII and no atlas feature
 * carries it, so its row here is derived from nothing and deleting it fails no test.
 * Adding a country in that position means adding its spelling here by hand, and knowing
 * that nothing will catch you if you do not.
 *
 * What the list is not is a guess at what a postcard might say. `origin_country` is a free
 * text field with only a datalist of suggestions behind it, and `CanonicalCountry` passes a
 * name it does not know through unchanged, accents and all.
 *
 * That used to be recorded here as a live hazard: an accented country typed by hand could
 * mint a link no entry answers for. Issue #44 asked for it to be closed, and looking at it
 * properly found it was not the hazard. A link is minted from `normalizedCountry` and read
 * back through `countryFromParam`, which normalizes the same way, so an accented name folds
 * identically on both sides and the link resolves — checked across every atlas name, every
 * alias, and a spread of hand-typed endonyms. Nothing here needed pre-filling, and the fix
 * #44 proposed (folding accents again inside `countryFromParam`) would never have run.
 *
 * What that search did turn up is above, in `normalizePlace`: the fold kept the letters NFD
 * could decompose and dropped the rest, so "Føroyar" lost its Ø. Rows invented for spellings
 * nobody has written would still be guesses in a table whose whole value is that every row
 * is derived — so the answer to #44 is a wider fold, not a wider table.
 *
 * The generated endonym table needs nothing here, for both of the reasons above at once: it
 * stores its keys already folded, and every one of them is a name that resolved to nothing
 * before #47, so no release of any fold ever minted a link from one.
 *
 * These are the names, not every variant of them. A postcard written "Åland" rather than
 * "Åland Islands" would have minted `?country=land`, and that stays broken on purpose:
 * `land` is far too generic a key to hand a table that answers before the name is looked
 * up, and it is nobody's spelling of the place — the `aland` alias covers writing it that
 * way from here on.
 */
export const accentedSpellings: readonly string[] = [
  "Côte d'Ivoire",
  "Türkiye",
  "Åland Islands",
  "Curaçao",
  "Réunion",
  "São Tomé and Príncipe",
  "Saint Barthélemy",
];

/**
 * What a `?country=` minted before the accent fold means now, keyed like an alias table.
 *
 * `aliasKey` did not already answer this. It takes spacing out of a spelling, and the old
 * fold did not merely space an accented name differently — it dropped the letter, so
 * "c te d ivoire" keys on `ctedivoire` against today's `cotedivoire`, one character short.
 * Every one of these names is short a letter the same way.
 *
 * This is deliberately not part of `countryAliases`. That table is spellings people write,
 * and nobody writes "cura ao"; these are artifacts of one release of one function, they
 * only ever reached the software through a URL, and keeping them here means the whole shim
 * can be deleted in one piece once the links are old enough to let go. The test holds them
 * to the one thing that makes the shim safe: no legacy key is a name anything resolves to
 * today, so consulting this table can never intercept a live one.
 *
 * Only `?country=` needs it. A `?region=` names one of the fifty states or DC — that is
 * what the state map draws and counts — and none of those is spelled with an accent.
 */
export const legacyCountryParams = new Map(
  accentedSpellings.map((written) => [aliasKey(foldWithoutAccents(written)), normalizedCountry(written)]),
);

/** `normalizePlace` exactly as it read before the accent fold: the letter went with its accent. */
function foldWithoutAccents(value: string) {
  return value.toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
}

export function countryFromParam(value: string) {
  const name = normalizedCountry(value);
  return legacyCountryParams.get(aliasKey(name)) ?? name;
}

export function stateFromParam(value: string) {
  return normalizedState(value);
}

/**
 * Where places the world map cannot draw actually are, as [longitude, latitude].
 *
 * The 110m atlas has 177 country shapes, and a place smaller than about a pixel at that
 * scale simply isn't one of them. Seventy-three of the missing ones are countries
 * Postcrossing lists, and seventy of those have sent mail — Singapore and Hong Kong
 * especially. Without a coordinate each of their postcards silently counted as zero miles
 * traveled. They still cannot be shaded on the map, but they can be named in the list and
 * counted in the distance.
 *
 * The other three — British Indian Ocean Territory, Tokelau and United States Minor
 * Outlying Islands — have sent none. They are here so that the list needs no exceptions,
 * not because anything has arrived from them.
 *
 * The list is an audit, not a guess, and its scope is every one of the 248 rows on the
 * Postcrossing countries table — not the ones with a current member. That filter is what
 * issue #32 asked for and it is the wrong one for this app: a member is someone who can
 * be sent an address today, while this is a record of postcards already received. Sint
 * Maarten has no members and has sent 626 cards; Palau none and 287. Those cards are in
 * the collection now, and under the member filter every one of them scored zero miles.
 * So there is no filter here, and postcrossing-senders.test.ts asserts the whole list
 * resolves — a completeness claim is worth making only if something checks it.
 *
 * There is no shape to take a centroid from, so each entry is its capital or main
 * settlement, which is where the mail is posted anyway.
 *
 * An overseas territory gets its own entry rather than an alias to the state that
 * administers it. Cayenne is 7,000 km from metropolitan France and Longyearbyen 2,000 km
 * from mainland Norway; aliasing either onto its sovereign would file the card under the
 * wrong country and measure it from the wrong continent.
 */
export const unmappedCoordinates: Record<string, [number, number]> = {
  "hong kong": [114.17, 22.32],
  macao: [113.54, 22.20],
  singapore: [103.82, 1.35],
  malta: [14.38, 35.90],
  liechtenstein: [9.55, 47.17],
  andorra: [1.52, 42.51],
  monaco: [7.42, 43.74],
  "san marino": [12.46, 43.94],
  bahrain: [50.55, 26.07],
  maldives: [73.51, 4.18],
  mauritius: [57.55, -20.35],
  bermuda: [-64.75, 32.31],
  guernsey: [-2.58, 49.45],
  jersey: [-2.11, 49.21],
  "isle of man": [-4.55, 54.24],
  "aland islands": [20.00, 60.18],
  "faroe islands": [-6.91, 61.89],
  gibraltar: [-5.35, 36.14],
  "vatican city": [12.45, 41.90],
  curacao: [-68.93, 12.09],
  reunion: [55.53, -21.15],
  "sao tome and principe": [6.61, 0.19],
  // Praia, on Santiago, rather than the middle of the archipelago: the islands span some
  // 250 km, and Santiago holds over half the population, so the mail comes from here.
  "cabo verde": [-23.51, 14.93],
  // The Caribbean: no island state or dependency here is a 110m shape.
  "antigua and barbuda": [-61.85, 17.12],
  aruba: [-70.03, 12.52],
  barbados: [-59.62, 13.10],
  "bonaire sint eustatius and saba": [-68.28, 12.15],
  "british virgin islands": [-64.62, 18.43],
  "cayman islands": [-81.38, 19.29],
  dominica: [-61.39, 15.30],
  grenada: [-61.75, 12.06],
  guadeloupe: [-61.73, 16.00],
  martinique: [-61.07, 14.60],
  "saint kitts and nevis": [-62.72, 17.30],
  "saint lucia": [-61.00, 14.01],
  "saint martin": [-63.08, 18.07],
  "saint vincent and the grenadines": [-61.23, 13.16],
  "united states virgin islands": [-64.93, 18.34],
  anguilla: [-63.06, 18.22],
  montserrat: [-62.21, 16.79],
  "saint barthelemy": [-62.85, 17.90],
  // Philipsburg, on the Dutch half of the island Marigot sits on: two countries, 12 km
  // apart, and the normalisation has to keep them apart because the gallery counts them.
  "sint maarten": [-63.05, 18.03],
  "turks and caicos islands": [-71.14, 21.46],
  // The Atlantic, and French Guiana on the South American coast with it.
  "french guiana": [-52.33, 4.94],
  "saint helena ascension and tristan da cunha": [-5.72, -15.93],
  "saint pierre and miquelon": [-56.18, 46.78],
  "south georgia and the south sandwich islands": [-36.50, -54.28],
  // The Pacific.
  "american samoa": [-170.70, -14.28],
  "cook islands": [-159.78, -21.21],
  "french polynesia": [-149.57, -17.54],
  guam: [144.75, 13.47],
  kiribati: [172.98, 1.33],
  "marshall islands": [171.38, 7.09],
  micronesia: [158.16, 6.92],
  nauru: [166.92, -0.55],
  niue: [-169.92, -19.06],
  "norfolk island": [167.96, -29.06],
  "northern mariana islands": [145.75, 15.21],
  palau: [134.62, 7.50],
  pitcairn: [-130.10, -25.07],
  samoa: [-171.77, -13.83],
  // Nukunonu, the largest atoll; Tokelau has no capital.
  tokelau: [-171.82, -9.19],
  tonga: [-175.20, -21.14],
  tuvalu: [179.20, -8.52],
  // A scattered bucket of uninhabited islands rather than a place, so any single point is
  // arbitrary. Midway is the only one of them that ever had a post office.
  "united states minor outlying islands": [-177.37, 28.21],
  "wallis and futuna": [-176.17, -13.28],
  // The Indian Ocean.
  "british indian ocean territory": [72.41, -7.31],
  "christmas island": [105.68, -10.42],
  "cocos keeling islands": [96.83, -12.19],
  comoros: [43.26, -11.70],
  mayotte: [45.23, -12.78],
  seychelles: [55.45, -4.62],
  // The Arctic: Longyearbyen, not the Norwegian mainland.
  "svalbard and jan mayen": [15.63, 78.22],
};

/**
 * Places whose atlas centroid is nowhere the mail comes from, as [longitude, latitude].
 *
 * `geoCentroid` of a country's whole multipolygon is faithful to the shape, and for a
 * country with far-flung territory that is the problem: French Guiana and the overseas
 * departments pull France's centroid about 800 km west into the Bay of Biscay, and
 * Svalbard pulls Norway's some 300 km north of the mainland. Every card from there was
 * measured from open sea. Each entry here is the centroid of the country's largest
 * polygon alone, and it wins over the atlas — unlike `unmappedCoordinates`, which only
 * fills in for shapes the atlas lacks.
 *
 * The rule for who gets an entry is mechanical, and the test holds the table to it: a
 * country whose largest polygon is at least four fifths of its area (so there is a
 * mainland to prefer) and whose whole-feature centroid sits more than 100 km from that
 * mainland's. That admits France, Norway, the United States, Chile (Tierra del Fuego)
 * and Papua New Guinea (its eastern islands). It excludes countries the atlas draws as
 * one polygon (Portugal without the Azores, the Netherlands without its Caribbean
 * municipalities) and archipelagos with no single mainland — Japan, Malaysia, New
 * Zealand, Indonesia, the Philippines — which stay on the atlas centroid between their
 * islands.
 *
 * Canada also meets the rule (its Arctic archipelago, 240 km) and is deliberately left
 * out, in `overrideExclusions` so the test still holds it to the rule. Its mainland
 * centroid is in northern Manitoba, as far from anyone's mailbox as the atlas one in
 * Nunavut: across Toronto, Calgary and Vancouver the two are a wash, and for Quebec and
 * Montreal the atlas one is the closer by 150 miles — and the collection's one Canadian
 * card is from Quebec. Canada's problem is centroid versus population, which only
 * measuring per province would fix, the way the United States is measured per state.
 *
 * The United States is measured per state when the region names one. The region is
 * optional, and "Brooklyn" is not a state, so a card can still fall through to the
 * country lookup — and Alaska and the Aleutians drag the whole-feature centroid some
 * 670 km north-west of the lower forty-eight's. The entry here is the lower forty-eight.
 */
export const overrideCoordinates: Record<string, [number, number]> = {
  france: [2.35, 46.56],
  norway: [12.37, 64.09],
  "united states of america": [-98.73, 39.87],
  chile: [-71.24, -35.88],
  "papua new guinea": [144.32, -6.64],
};

/**
 * Countries the displacement rule selects that stay on the atlas centroid anyway, each
 * with its reason above. The test checks these still meet the rule, so an exclusion
 * cannot outlive the atlas geometry that made it necessary.
 */
export const overrideExclusions: readonly string[] = ["canada"];

/**
 * Where a postcard from `country` (already normalized) starts its journey, or undefined
 * when nothing knows. The order is the point: a hand-placed override beats the atlas
 * centroid, and the atlas beats the fallback table for places it cannot draw.
 *
 * All three lookups are exact, and stay exact. Spacing is settled one step earlier, in
 * `normalizedCountry`, which is where the name arrives from — so a card written "Viet Nam"
 * asks here for "vietnam". Making these three tolerate spacing as well would be a second
 * answer to a question already answered, and the one that runs would be whichever the
 * caller happened to reach.
 */
export function countryCoordinate(
  country: string,
  atlas: ReadonlyMap<string, [number, number]>,
): [number, number] | undefined {
  return overrideCoordinates[country] ?? atlas.get(country) ?? unmappedCoordinates[country];
}

/** Title case for display: "united kingdom" reads as "United Kingdom". */
export function titleCase(normalized: string) {
  return normalized.replace(/\b[a-z]/g, (letter) => letter.toUpperCase());
}
